
import { 
  CapTableEntry, 
  FundingRound, 
  CapitalManagement,
  TaxLiabilityItem,
  TaxBenefitItem
} from '@/lib/types';

/**
 * Calculate dilution for all shareholders based on a funding round
 * @param capTable - Current cap table
 * @param fundingRound - New funding round details
 * @returns Updated cap table with dilution
 */
export function calculateDilution(
  capTable: CapTableEntry[], 
  fundingRound: FundingRound
): CapTableEntry[] {
  const totalExistingShares = capTable.reduce((sum, entry) => sum + entry.sharesIssued, 0);
  const totalSharesAfterRound = totalExistingShares + fundingRound.newSharesIssued;
  
  return capTable.map(entry => {
    const currentOwnership = entry.sharesIssued / totalExistingShares;
    const dilutedOwnership = (entry.sharesIssued / totalSharesAfterRound) * 100;
    
    return {
      ...entry,
      dilutedOwnership,
      // Recalculate value based on new valuation
      value: (entry.sharesIssued / totalSharesAfterRound) * fundingRound.postMoneyValuation
    };
  });
}

/**
 * Calculate vesting status for all shareholders
 * @param capTable - Current cap table
 * @param asOfDate - Date to calculate vesting for (default: today)
 * @returns Updated cap table with vesting calculations
 */
export function calculateVesting(
  capTable: CapTableEntry[], 
  asOfDate: Date = new Date()
): CapTableEntry[] {
  return capTable.map(entry => {
    // If no vesting schedule, consider fully vested
    if (!entry.vestingSchedule) {
      return {
        ...entry,
        vestingSchedule: {
          ...entry.vestingSchedule,
          percentVested: 100
        }
      };
    }
    
    const startDate = new Date(entry.vestingSchedule.vestingStartDate);
    const cliffDate = new Date(startDate);
    cliffDate.setMonth(cliffDate.getMonth() + entry.vestingSchedule.cliffPeriod);
    
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + entry.vestingSchedule.vestingPeriod);
    
    // Before cliff date
    if (asOfDate < cliffDate) {
      return {
        ...entry,
        vestingSchedule: {
          ...entry.vestingSchedule,
          percentVested: 0
        }
      };
    }
    
    // After full vesting
    if (asOfDate >= endDate) {
      return {
        ...entry,
        vestingSchedule: {
          ...entry.vestingSchedule,
          percentVested: 100
        }
      };
    }
    
    // During vesting period after cliff
    const totalMonths = entry.vestingSchedule.vestingPeriod;
    const elapsedMonths = ((asOfDate.getFullYear() - startDate.getFullYear()) * 12) + 
                          (asOfDate.getMonth() - startDate.getMonth());
    
    // Apply linear vesting after cliff
    const monthsAfterCliff = elapsedMonths - entry.vestingSchedule.cliffPeriod;
    const monthsVesting = totalMonths - entry.vestingSchedule.cliffPeriod;
    const percentVested = Math.min(100, (monthsAfterCliff / monthsVesting) * 100);
    
    return {
      ...entry,
      vestingSchedule: {
        ...entry.vestingSchedule,
        percentVested
      }
    };
  });
}

/**
 * Calculate cash runway based on current balance and burn rate
 * @param cashBalance - Current cash balance
 * @param monthlyBurn - Monthly burn rate (negative for spending)
 * @returns Runway in months
 */
export function calculateRunway(cashBalance: number, monthlyBurn: number): number {
  // Ensure burn rate is positive for calculation (cash outflow)
  const burnRate = Math.abs(monthlyBurn);
  
  if (burnRate === 0) {
    return Infinity; // No burn, infinite runway
  }
  
  return cashBalance / burnRate;
}

/**
 * Calculate fully diluted ownership percentages
 * @param capTable - Current cap table
 * @returns Updated cap table with fully diluted percentages
 */
export function calculateFullyDilutedOwnership(capTable: CapTableEntry[]): CapTableEntry[] {
  const totalShares = capTable.reduce((sum, entry) => sum + entry.sharesIssued, 0);
  
  return capTable.map(entry => ({
    ...entry,
    fullyDilutedOwnership: (entry.sharesIssued / totalShares) * 100
  }));
}

/**
 * Calculate NZ company tax liability based on mining operations
 * @param taxableIncome - Taxable income for the year
 * @param taxRate - Current corporate tax rate (default 28% for NZ)
 * @param taxCredits - Any applicable tax credits
 * @returns Net tax liability
 */
export function calculateNZTaxLiability(
  taxableIncome: number, 
  taxRate: number = 0.28, 
  taxCredits: number = 0
): number {
  const grossTax = taxableIncome * taxRate;
  return Math.max(0, grossTax - taxCredits);
}

/**
 * Calculate exploration expenditure tax treatment under NZ tax law
 * @param expenditure - Exploration cost amount
 * @param hasProvedResource - Whether a proved mineral resource exists
 * @returns Tax benefit amount and timing
 */
export function calculateExplorationTaxBenefit(
  expenditure: number,
  hasProvedResource: boolean
): { deductibleAmount: number; capitalizedAmount: number; depreciationRate: number } {
  // In NZ, exploration expenditure is generally deductible
  // If it results in a proved mineral resource, it may be capitalized and depreciated
  
  if (!hasProvedResource) {
    // Fully deductible in current year
    return {
      deductibleAmount: expenditure,
      capitalizedAmount: 0,
      depreciationRate: 0
    };
  } else {
    // In NZ when a proved resource results, the expenditure may be capitalized
    // and depreciated at a rate dependent on mine life (using 10% as example)
    return {
      deductibleAmount: 0,
      capitalizedAmount: expenditure,
      depreciationRate: 0.10 // 10% straight line as example
    };
  }
}

/**
 * Calculate development expenditure tax treatment under NZ tax law
 * @param expenditure - Development cost amount
 * @param estimatedMineLife - Estimated mine life in years
 * @returns Depreciation rate and schedule
 */
export function calculateDevelopmentTaxTreatment(
  expenditure: number,
  estimatedMineLife: number
): { depreciationRate: number; annualDeduction: number } {
  // In NZ, mining development expenditure is typically capitalized and depreciated
  // based on estimated mine life or standard rates
  
  // Using straight-line method based on mine life
  const depreciationRate = 1 / Math.max(1, estimatedMineLife);
  const annualDeduction = expenditure * depreciationRate;
  
  return {
    depreciationRate,
    annualDeduction
  };
}

/**
 * Calculate rehabilitation provision tax treatment
 * @param provisionAmount - Amount set aside for rehabilitation
 * @returns Tax deductibility information
 */
export function calculateRehabilitationTaxTreatment(
  provisionAmount: number
): { currentDeduction: number; futureDeduction: number } {
  // In NZ, mining rehabilitation provisions are generally only deductible when incurred
  // rather than when provisioned (but this is simplified)
  return {
    currentDeduction: 0, // Generally not immediately deductible when provisioned
    futureDeduction: provisionAmount // Deductible when actually spent
  };
}

/**
 * Generate projected annual tax liabilities
 * @param annualIncome - Array of projected annual taxable income
 * @param taxRate - Corporate tax rate (default 28% for NZ)
 * @param taxCredits - Array of projected tax credits
 * @returns Array of annual tax liabilities
 */
export function generateTaxProjections(
  annualIncome: number[],
  taxRate: number = 0.28,
  taxCredits: number[] = []
): TaxLiabilityItem[] {
  return annualIncome.map((income, index) => {
    const year = new Date().getFullYear() + index;
    const credits = taxCredits[index] || 0;
    const taxPayable = income * taxRate;
    const netLiability = Math.max(0, taxPayable - credits);
    
    return {
      id: `tax-${year}`,
      year,
      taxableIncome: income,
      taxRate: taxRate * 100, // Convert to percentage
      taxPayable,
      taxCredits: credits,
      netTaxLiability: netLiability
    };
  });
}
